import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { formatPriceFR } from '../utils/formatFR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
};

/**
 * Marketing Page — Hub avec 5 onglets (Dashboard, Campagnes, Réseaux sociaux, Analytics, Chatbot).
 *
 * Synchronisation URL :
 *   - /marketing?tab=dashboard     (défaut)
 *   - /marketing?tab=campaigns
 *   - /marketing?tab=social
 *   - /marketing?tab=analytics
 *   - /marketing?tab=chatbot
 *
 * Compatible aussi avec les anciens chemins du sidebar `/marketing/social`, `/marketing/campaigns`, …
 *
 * Design sobre, cohérent avec le reste de l'application (blanc / slate / blue-600).
 */

const TABS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: 'bi-grid-fill' },
  { id: 'campaigns', label: 'Campagnes', icon: 'bi-megaphone-fill' },
  { id: 'social', label: 'Réseaux sociaux', icon: 'bi-share-fill' },
  // Onglets « Analytics » et « Chatbot » retirés (démo-ready) :
  // placeholders non implémentés.
];

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: 'bi-facebook', color: 'bg-blue-600' },
  { value: 'instagram', label: 'Instagram', icon: 'bi-instagram', color: 'bg-pink-500' },
  { value: 'linkedin', label: 'LinkedIn', icon: 'bi-linkedin', color: 'bg-blue-700' },
  { value: 'twitter', label: 'Twitter / X', icon: 'bi-twitter-x', color: 'bg-slate-900' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Brouillon', color: 'bg-amber-100 text-amber-700' },
  { value: 'scheduled', label: 'Programmé', color: 'bg-blue-100 text-blue-700' },
  { value: 'published', label: 'Publié', color: 'bg-emerald-100 text-emerald-700' },
];

const CAMPAIGN_TYPES = [
  { value: 'sponsored_post', label: 'Post sponsorisé' },
  { value: 'email_marketing', label: 'Email marketing' },
  { value: 'sms_marketing', label: 'SMS marketing' },
  { value: 'display_ads', label: "Display Ads" },
];

// ============== Modal Nouveau Post ==============
const NewPostModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    platform: 'facebook',
    status: 'draft',
    title: '',
    description: '',
    hashtags: '',
    property_id: '',
    tone: 'professional',
  });
  const [submitting, setSubmitting] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/properties?status=active`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setProperties(d || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleGenerateAI = async () => {
    if (!form.property_id) {
      alert('Veuillez d\'abord sélectionner un bien pour générer le contenu avec l\'IA.');
      return;
    }
    setGeneratingAI(true);
    try {
      const res = await fetch(`${API_URL}/api/marketing/generate-post`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          property_id: form.property_id,
          platform: form.platform,
          tone: form.tone,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        const d = data.detail;
        const msg = typeof d === 'string' ? d : (d?.message || 'Erreur de génération.');
        throw new Error(msg);
      }
      const post = data.post || {};
      setForm((f) => ({
        ...f,
        description: post.content || '',
        hashtags: (post.hashtags || []).map((h) => h.replace(/^#/, '')).join(' '),
        title: f.title || `Post ${PLATFORMS.find((p) => p.value === f.platform)?.label || ''} — ${post.property_reference || ''}`.trim(),
      }));
    } catch (e) {
      alert(`Erreur IA : ${e.message}`);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      alert('Le titre est requis.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        platform: form.platform,
        status: form.status,
        title: form.title.trim(),
        description: form.description || '',
        hashtags: form.hashtags
          .split(/\s+/)
          .map((h) => h.replace(/^#/, ''))
          .filter(Boolean),
        created_by: 'web',
      };
      if (form.property_id) payload.property_id = form.property_id;
      const res = await fetch(`${API_URL}/api/marketing/social-posts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      onCreated?.();
      onClose();
    } catch (e) {
      alert(`Erreur création post : ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="marketing-new-post-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <i className="bi bi-share-fill text-emerald-600 text-lg"></i>
            </div>
            <h3 className="text-base font-bold text-slate-800">Nouveau Post Réseaux Sociaux</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition" data-testid="marketing-new-post-close">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Plateforme</label>
              <select
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="marketing-new-post-platform"
              >
                {PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Statut</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="marketing-new-post-status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bloc IA (Bien lié + Ton + bouton Générer) */}
          <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2">
              <i className="bi bi-magic text-blue-600"></i>
              <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Génération IA (optionnel)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Bien concerné</label>
                <select
                  value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  data-testid="marketing-new-post-property"
                >
                  <option value="">— Aucun —</option>
                  {properties.map((p) => {
                    const ville = p.bien_general?.ville_generale || p.localisation?.ville || p.informations_confidentielles?.ville || '';
                    const titre = p.bien_general?.titre_annonce || p.objet || p.profil || 'Bien';
                    return (
                      <option key={p.id} value={p.id}>
                        {p.reference ? `${p.reference} · ` : ''}{titre}{ville ? ` · ${ville}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Ton</label>
                <select
                  value={form.tone}
                  onChange={(e) => setForm({ ...form, tone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  data-testid="marketing-new-post-tone"
                >
                  <option value="professional">Professionnel</option>
                  <option value="casual">Décontracté</option>
                  <option value="enthusiastic">Enthousiaste</option>
                </select>
              </div>
            </div>
            <button
              type="button"
              onClick={handleGenerateAI}
              disabled={generatingAI || !form.property_id}
              className="w-full px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center justify-center gap-2"
              data-testid="marketing-new-post-generate-ai"
              title={!form.property_id ? 'Sélectionnez un bien pour activer la génération IA' : ''}
            >
              <i className={`bi ${generatingAI ? 'bi-arrow-clockwise animate-spin' : 'bi-magic'}`}></i>
              {generatingAI ? 'Génération en cours…' : 'Générer le contenu avec l\'IA'}
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Titre du post <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex : Nouveau restaurant disponible à Monaco"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="marketing-new-post-title"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Contenu du post</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={6}
              placeholder="Rédigez votre post… ou utilisez la génération IA ci-dessus"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              data-testid="marketing-new-post-content"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Hashtags</label>
            <input
              type="text"
              value={form.hashtags}
              onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
              placeholder="Ex : #restaurant #monaco #immobilier #pizzeria"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="marketing-new-post-hashtags"
            />
            <p className="text-[11px] text-slate-500 mt-1">Séparez les hashtags par des espaces.</p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition" data-testid="marketing-new-post-cancel">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.title.trim()}
            className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center gap-2"
            data-testid="marketing-new-post-submit"
          >
            <i className="bi bi-check-lg"></i>
            {submitting ? 'Création…' : 'Créer le post'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Modal Nouvelle Campagne ==============
const NewCampaignModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    type: 'sponsored_post',
    description: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    budget: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      alert('Le nom de la campagne est requis.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        description: form.description || null,
        start_date: new Date(form.start_date).toISOString(),
      };
      if (form.end_date) payload.end_date = new Date(form.end_date).toISOString();
      if (form.budget) payload.budget = parseFloat(form.budget);

      const res = await fetch(`${API_URL}/api/marketing/campaigns`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      onCreated?.();
      onClose();
    } catch (e) {
      alert(`Erreur création campagne : ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="marketing-new-campaign-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <i className="bi bi-megaphone-fill text-blue-600 text-lg"></i>
            </div>
            <h3 className="text-base font-bold text-slate-800">Nouvelle Campagne</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition" data-testid="marketing-new-campaign-close">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Nom de la campagne <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex : Promo printemps Monaco"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="marketing-new-campaign-name"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="marketing-new-campaign-type"
            >
              {CAMPAIGN_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Début</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="marketing-new-campaign-start"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Fin (optionnel)</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="marketing-new-campaign-end"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Budget (€, optionnel)</label>
            <input
              type="number"
              min="0"
              step="50"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              placeholder="Ex : 500"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="marketing-new-campaign-budget"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Description (optionnel)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Objectifs, cibles, message clé…"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              data-testid="marketing-new-campaign-description"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition" data-testid="marketing-new-campaign-cancel">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.name.trim()}
            className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center gap-2"
            data-testid="marketing-new-campaign-submit"
          >
            <i className="bi bi-check-lg"></i>
            {submitting ? 'Création…' : 'Créer la campagne'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Placeholder réutilisable ==============
const Placeholder = ({ icon, title, description }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-8 sm:p-12 shadow-sm">
    <div className="max-w-2xl mx-auto text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-50 border border-blue-100 mb-5">
        <i className={`bi ${icon} text-3xl sm:text-4xl text-blue-600`}></i>
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">{title}</h2>
      <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
      <div className="mt-6">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide bg-amber-500/10 text-amber-700 border border-amber-200">
          <i className="bi bi-clock-history"></i> Bientôt disponible
        </span>
      </div>
    </div>
  </div>
);

// ============== Composant principal ==============
const MarketingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Détection du tab actif via ?tab= OU via /marketing/<slug>
  const activeTab = useMemo(() => {
    const fromQuery = searchParams.get('tab');
    if (fromQuery && TABS.find((t) => t.id === fromQuery)) return fromQuery;
    const pathTail = location.pathname.replace(/^\/marketing\/?/, '');
    const matched = TABS.find((t) => t.id === pathTail);
    return matched ? matched.id : 'dashboard';
  }, [searchParams, location.pathname]);

  const setTab = (tabId) => setSearchParams({ tab: tabId });

  const [posts, setPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewPost, setShowNewPost] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketing/social-posts`, { headers: getAuthHeaders() });
      if (res.ok) setPosts(await res.json());
      else setPosts([]);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketing/campaigns`, { headers: getAuthHeaders() });
      if (res.ok) setCampaigns(await res.json());
      else setCampaigns([]);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'social' || activeTab === 'dashboard') loadPosts();
    if (activeTab === 'campaigns' || activeTab === 'dashboard') loadCampaigns();
  }, [activeTab, loadPosts, loadCampaigns]);

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/marketing/posts/${postId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) loadPosts();
    } catch (e) {
      alert(`Erreur : ${e.message}`);
    }
  };

  const stats = {
    posts: posts.length,
    campaigns: campaigns.length,
    drafts: posts.filter((p) => p.status === 'draft').length,
    published: posts.filter((p) => p.status === 'published').length,
  };

  // ============== Render content par onglet ==============
  const renderContent = () => {
    switch (activeTab) {
      case 'campaigns':
        return (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <i className="bi bi-megaphone-fill text-blue-600"></i>
                Campagnes marketing
              </h3>
              <span className="text-xs text-slate-500">{campaigns.length} campagne(s)</span>
            </div>
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-500">
                <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Chargement…
              </div>
            ) : campaigns.length === 0 ? (
              <div className="p-8 text-center">
                <i className="bi bi-inbox text-4xl text-slate-300 block mb-3"></i>
                <p className="text-sm text-slate-600 mb-4">Aucune campagne pour le moment.</p>
                <button onClick={() => setShowNewCampaign(true)} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition" data-testid="marketing-campaigns-empty-create">
                  <i className="bi bi-plus-lg mr-1"></i>Créer une campagne
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <li key={c.id} className="p-4 hover:bg-slate-50 transition" data-testid={`marketing-campaign-row-${c.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {CAMPAIGN_TYPES.find((t) => t.value === c.type)?.label || c.type}
                          {c.budget ? <> · Budget <span className="num">{formatPriceFR(c.budget)}</span></> : ''}
                        </div>
                        {c.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.description}</p>}
                      </div>
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );

      case 'social':
        return (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <i className="bi bi-share-fill text-emerald-600"></i>
                Publications réseaux sociaux
              </h3>
              <span className="text-xs text-slate-500">{posts.length} post(s)</span>
            </div>
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-500">
                <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Chargement…
              </div>
            ) : posts.length === 0 ? (
              <div className="p-8 text-center">
                <i className="bi bi-share text-4xl text-slate-300 block mb-3"></i>
                <p className="text-sm text-slate-600 mb-4">Aucun post pour le moment.</p>
                <button onClick={() => setShowNewPost(true)} className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition" data-testid="marketing-social-empty-create">
                  <i className="bi bi-plus-lg mr-1"></i>Créer le premier post
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {posts.map((p) => {
                  const platform = PLATFORMS.find((pl) => pl.value === p.platform) || { label: p.platform, icon: 'bi-globe', color: 'bg-slate-500' };
                  const status = STATUS_OPTIONS.find((s) => s.value === p.status) || STATUS_OPTIONS[0];
                  return (
                    <li key={p.id} className="p-4 hover:bg-slate-50 transition" data-testid={`marketing-post-row-${p.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg ${platform.color} text-white flex items-center justify-center shrink-0`}>
                          <i className={`bi ${platform.icon} text-base`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-slate-800 truncate">{p.title || '(sans titre)'}</div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{platform.label}</div>
                          {p.description && <p className="text-xs text-slate-600 mt-1.5 line-clamp-2 whitespace-pre-wrap">{p.description}</p>}
                          {p.hashtags?.length > 0 && (
                            <div className="text-[11px] text-blue-600 mt-1.5">{p.hashtags.map((h) => `#${h}`).join(' ')}</div>
                          )}
                        </div>
                        <button onClick={() => handleDeletePost(p.id)} className="text-slate-400 hover:text-red-500 transition p-1" title="Supprimer">
                          <i className="bi bi-trash text-sm"></i>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );

      // Cases « analytics » et « chatbot » retirées (onglets non affichés).

      case 'dashboard':
      default:
        return (
          <div className="space-y-4">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Campagnes', value: stats.campaigns, icon: 'bi-megaphone-fill', color: 'blue' },
                { label: 'Posts', value: stats.posts, icon: 'bi-share-fill', color: 'emerald' },
                { label: 'Brouillons', value: stats.drafts, icon: 'bi-pencil', color: 'amber' },
                { label: 'Publiés', value: stats.published, icon: 'bi-check-circle', color: 'violet' },
              ].map((k) => (
                <div key={k.label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide">{k.label}</div>
                      <div className="text-2xl font-bold text-slate-800 mt-1">{k.value}</div>
                    </div>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${k.color}-100`}>
                      <i className={`bi ${k.icon} text-${k.color}-600 text-lg`}></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Activité récente */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <i className="bi bi-megaphone-fill text-blue-600"></i>Campagnes récentes
                  </h3>
                  <button onClick={() => setTab('campaigns')} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    Tout voir <i className="bi bi-arrow-right ml-1"></i>
                  </button>
                </div>
                {campaigns.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Aucune campagne</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {campaigns.slice(0, 5).map((c) => (
                      <li key={c.id} className="p-3 text-sm text-slate-700 truncate">{c.name}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <i className="bi bi-share-fill text-emerald-600"></i>Posts récents
                  </h3>
                  <button onClick={() => setTab('social')} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    Tout voir <i className="bi bi-arrow-right ml-1"></i>
                  </button>
                </div>
                {posts.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Aucun post</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {posts.slice(0, 5).map((p) => (
                      <li key={p.id} className="p-3 text-sm text-slate-700 truncate">{p.title || '(sans titre)'}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-4 bg-slate-50 min-h-screen" data-testid="marketing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <i className="bi bi-megaphone text-blue-600 text-xl sm:text-2xl"></i>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Marketing</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Gestion des campagnes et réseaux sociaux</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNewCampaign(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition flex items-center gap-2" data-testid="marketing-new-campaign-btn">
            <i className="bi bi-plus-circle-fill"></i>
            <span>Nouvelle campagne</span>
          </button>
          <button onClick={() => setShowNewPost(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold shadow-sm transition flex items-center gap-2" data-testid="marketing-new-post-btn">
            <i className="bi bi-share-fill"></i>
            <span>Nouveau post</span>
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2" role="tablist">
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                role="tab"
                aria-selected={isActive}
                data-testid={`marketing-tab-${t.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${
                  isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <i className={`bi ${t.icon} text-xs`}></i>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu */}
      {renderContent()}

      {/* Modals */}
      {showNewPost && <NewPostModal onClose={() => setShowNewPost(false)} onCreated={() => { loadPosts(); setTab('social'); }} />}
      {showNewCampaign && <NewCampaignModal onClose={() => setShowNewCampaign(false)} onCreated={() => { loadCampaigns(); setTab('campaigns'); }} />}
    </div>
  );
};

export default MarketingPage;
// eslint-disable-next-line no-unused-vars
const _NAV_HELPER = (navigate) => navigate('/marketing'); // gardé pour usages futurs
